//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { ILogService } from 'angular';
import { PojoMap } from 'pojo-maps';
import RX, { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import _ from 'underscore';

import { COLLECTION_SETTINGS_FILTERS } from '@search-filters/constants/collectionSettingsFilters';
import { searchFilterUpdates } from '@search-filters/constants/searchFilterUpdates';
import { SEARCH_FILTERS as SearchFiltersConst } from '@search-filters/constants/searchFilters';
import { ITrackingService } from '@services/tracking-service';

import { SessionVarsService as ISessionVarsService } from 'app/services/session-vars-service';

type SearchFilterSetNames = keyof typeof SearchFiltersConst;
type SearchFilterSetValues = typeof SearchFiltersConst[SearchFilterSetNames];
type SearchFilterKeyNames = keyof typeof COLLECTION_SETTINGS_FILTERS;

type SearchFilterUpdateKeys = keyof typeof searchFilterUpdates;
type SearchFilterUpdateValues = typeof searchFilterUpdates[Exclude<
  SearchFilterUpdateKeys,
  'UPDATES'
>];

interface FoldingKey {
  values: unknown[];
  alias: string;
}

interface SearchFilterRegisteredSet {
  params: PojoMap<string, unknown[]>;
  comparators: Record<string, (args: unknown) => unknown>;
  foldingKeys?: FoldingKey[];
  excludedKeys?: string[];
}

interface SetCategory {
  key: string;
  options: unknown[];
}

interface FilterSetObservable {
  setName: SearchFilterSetValues;
  setObject: PojoMap<string, unknown[]>;
  setCategories: SetCategory[];
  filterCount: number;
}

interface AliasObservableValue {
  setName: SearchFilterSetValues;
  filterKeyAliases: Record<string, string>;
  filterOptionAliases: Record<string, string>;
  foldingKeys: FoldingKey[];
}
export class SearchFilterService {
  constructor(
    private readonly $log: ILogService,
    private readonly rx: typeof RX,
    private readonly SearchFilterUpdates: typeof searchFilterUpdates,
    private readonly SEARCH_FILTERS: typeof SearchFiltersConst,
    private readonly SessionVarsService: ISessionVarsService,
    private readonly TrackingService: ITrackingService,
  ) {
    this.initFilterSetObservables();
  }
  private registeredSets: PojoMap<
    SearchFilterSetValues,
    SearchFilterRegisteredSet
  > = {};

  /**
   * This observable allows components to subscribe to search filter updates. All search filter updates that this service receives are published
   * to this observable. The next of the observable is called with an object with setName and setObject fields. The subscription can be filtered
   * by the setName. The setObject contains the new value of the filter.
   */
  private filterSetObservable!: BehaviorSubject<FilterSetObservable>;
  private aliasObservable!: ReplaySubject<AliasObservableValue>;
  private allFilterSetsObservable!: BehaviorSubject<
    PojoMap<SearchFilterSetValues, SearchFilterRegisteredSet>
  >;
  private allFilterSets!: BehaviorSubject<FilterSetObservable>;

  public $onInit() {
    this.initFilterSetObservables();
  }

  public initFilterSetObservables() {
    this.filterSetObservable = new this.rx.BehaviorSubject<FilterSetObservable>(
      {
        setName: 'initial',
        setObject: PojoMap.empty(),
        setCategories: [],
        filterCount: 0,
      },
    );
    this.aliasObservable = new this.rx.ReplaySubject<AliasObservableValue>();
    this.allFilterSets = new this.rx.BehaviorSubject<FilterSetObservable>({
      setName: 'initial',
      setObject: PojoMap.empty(),
      setCategories: [],
      filterCount: 0,
    });
    this.allFilterSetsObservable = new this.rx.BehaviorSubject<
      PojoMap<SearchFilterSetValues, SearchFilterRegisteredSet>
    >(PojoMap.empty());

    const internal = (
      this.allFilterSets as Observable<FilterSetObservable>
    ).scan((state, setUpdate) => {
      const newState = {
        ...state,
        [setUpdate.setName]: setUpdate,
      };
      return newState;
    }, {});
    this.filterSetObservable.subscribe(this.allFilterSets);
    internal.subscribe(this.allFilterSetsObservable);
  }

  private filterKeyAliases: PojoMap<
    SearchFilterSetValues,
    Record<string, string>
  > = {};
  private filterOptionAliases: PojoMap<
    SearchFilterSetValues,
    Record<string, string>
  > = {};
  private lockedKeys: PojoMap<SearchFilterSetValues, unknown[]> = {};

  public getRegisteredSet(setName: SearchFilterSetValues) {
    return this.registeredSets[setName];
  }

  public getFilterValue(
    setName: SearchFilterSetValues,
    filterKey: SearchFilterKeyNames,
  ) {
    const registeredFilterSet = this.registeredSets[setName];
    if (_.isUndefined(registeredFilterSet)) {
      return undefined;
    }
    return this.registeredSets[setName]?.params[filterKey];
  }

  public setRegisteredSet(
    setName: SearchFilterSetValues,
    setObject: SearchFilterRegisteredSet,
  ) {
    this.registeredSets = PojoMap.set(this.registeredSets, setName, setObject);
  }

  /**
   * Underscore types for chain/compact are incomplete. According to the docs,
   * compact should remove all falsy values from an array. However, passing <SetCategory | null>
   * as the type argument to `map` throws a type error:
   *
   * "SetCategory | null is not assignable to SetCategory"
   *
   * This necessitates type coercion
   */
  public calculateSetCategories(
    setName: SearchFilterSetValues,
    newValue: PojoMap<string, unknown[]>,
  ): SetCategory[] {
    return _.chain(_(newValue).keys())
      .map((filterKey) => {
        let currentState = angular.copy(newValue[filterKey]);
        const registeredSet = this.registeredSets[setName];
        const foldingKeys = registeredSet?.foldingKeys;
        const excludedKeys = registeredSet?.excludedKeys;

        if (_.isEmpty(currentState) || _(excludedKeys).contains(filterKey)) {
          return null;
        }

        _(foldingKeys).each((foldingKey) => {
          if (currentState) {
            const currentFoldingKeyValues = _(foldingKey.values).intersection(
              currentState,
            );

            if (currentFoldingKeyValues.length === foldingKey.values.length) {
              currentState = _(currentState).reject((option) => {
                return _(foldingKey.values).contains(option);
              });
              currentState.push(foldingKey.alias);
            }
          }
        });

        return {
          key: filterKey,
          options: currentState,
        };
      })
      .compact()
      .value() as unknown as SetCategory[];
  }

  private getFilterCount(setCategories: SetCategory[]) {
    return _(setCategories).reduce((memo, newCategory) => {
      return newCategory.options.length + memo;
    }, 0);
  }

  public publishAliasObservableUpdates(setName: SearchFilterSetValues) {
    this.aliasObservable.next({
      filterKeyAliases: this.filterKeyAliases[setName] ?? {},
      filterOptionAliases: this.filterOptionAliases[setName] ?? {},
      foldingKeys: this.registeredSets[setName]?.foldingKeys ?? [],
      setName,
    });
  }

  public registerNewFilterSet(
    setName: SearchFilterSetValues,
    paramsObject: PojoMap<string, unknown[]> = PojoMap.empty(),
    comparatorObject?: Record<string, () => void>,
  ) {
    if (this.getRegisteredSet(setName)) {
      throw new Error(`Filter set already registered with name=${setName}`);
    }
    this.setRegisteredSet(setName, {
      params: paramsObject,
      comparators: comparatorObject ?? {},
    });

    this.filterKeyAliases = PojoMap.set(this.filterKeyAliases, setName, {});
    this.filterOptionAliases = PojoMap.set(
      this.filterOptionAliases,
      setName,
      {},
    );
    this.lockedKeys = PojoMap.set(this.lockedKeys, setName, []);
    const setCategories = this.calculateSetCategories(setName, paramsObject);
    const filterCount = this.getFilterCount(setCategories);
    this.filterSetObservable.next({
      setName,
      setObject: paramsObject,
      setCategories,
      filterCount,
    });
  }

  public unregisterFilterSet(setName: SearchFilterSetValues) {
    const existingSet = this.getRegisteredSet(setName);
    if (existingSet) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.registeredSets[setName];
    }
  }

  public unregisterAllFilterSets() {
    Object.values(this.SEARCH_FILTERS).forEach((filterSetValue) =>
      this.unregisterFilterSet(filterSetValue),
    );
    this.initFilterSetObservables();
  }

  public updateSet(
    setName: SearchFilterSetValues,
    setObject: PojoMap<string, unknown[]>,
  ) {
    const setCategories = this.calculateSetCategories(setName, setObject);
    const filterCount = this.getFilterCount(setCategories);

    const registeredSet = this.getRegisteredSet(setName);
    if (registeredSet) {
      registeredSet.params = setObject;
    }

    this.filterSetObservable.next({
      setName,
      setObject,
      setCategories,
      filterCount,
    });
  }

  public addFilterValue(
    setName: SearchFilterSetValues,
    filterKey: string,
    filterValue: string | Record<string, unknown>,
  ) {
    const registeredFilterSet = this.getRegisteredSet(setName);
    if (!registeredFilterSet) {
      return;
    }
    const newSetObject = angular.copy(registeredFilterSet.params);
    const valueAlreadyInFilter = _(newSetObject[filterKey]).contains(
      filterValue,
    );
    if (!valueAlreadyInFilter) {
      newSetObject[filterKey]?.push(filterValue);
      this.updateSet(setName, newSetObject);
    }
  }

  public removeFilterValue(
    setName: SearchFilterSetValues,
    filterKey: string,
    filterValue: unknown,
  ) {
    const registeredFilterSet = this.getRegisteredSet(setName);
    if (!registeredFilterSet) {
      return;
    }
    const newSetObject = angular.copy(registeredFilterSet.params);
    const valueAlreadyInFilter = _(newSetObject[filterKey]).contains(
      filterValue,
    );
    if (valueAlreadyInFilter) {
      const newFilterValues = _(newSetObject[filterKey]).reject((value) => {
        return value === filterValue;
      });
      const updatedSetObject = PojoMap.set(
        newSetObject,
        filterKey,
        newFilterValues,
      );
      this.updateSet(setName, updatedSetObject);
    }
  }

  public transformFilterSet(
    newSetName: SearchFilterSetValues,
    existingSetName: SearchFilterSetValues,
    transformFn: (arg: FilterSetObservable) => PojoMap<string, unknown[]>,
  ) {
    this.unregisterFilterSet(newSetName);
    this.registerNewFilterSet(newSetName);

    this.getObservableForFilterSet(existingSetName).subscribe((setUpdate) => {
      this.updateSet(newSetName, transformFn(setUpdate));
    });
  }

  public getFilterSetObservable(): Observable<FilterSetObservable> {
    return this.filterSetObservable as Observable<FilterSetObservable>;
  }

  public getObservableForFilterSet(setName: SearchFilterSetValues) {
    return (this.filterSetObservable as Observable<FilterSetObservable>).filter(
      (setUpdate) => {
        return setUpdate.setName === setName;
      },
    );
  }

  public getFlattenedObservableForFilterSet(setName: SearchFilterSetValues) {
    return (
      this.allFilterSetsObservable as Observable<
        PojoMap<SearchFilterSetValues, SearchFilterRegisteredSet>
      >
    )
      .map((all) => all[setName])
      .filter((update) => !!update);
  }

  public getAliasObservable() {
    return this.aliasObservable;
  }

  public getAliasObservableForFilterSet(setName: SearchFilterSetValues) {
    return (this.aliasObservable as Observable<AliasObservableValue>).filter(
      (setUpdate) => {
        return setUpdate && setUpdate.setName === setName;
      },
    );
  }

  public registerFilterKeyAlias(
    setName: SearchFilterSetValues,
    filterKey: string,
    alias: string,
  ) {
    const filterKeyAlias = this.filterKeyAliases[setName];
    if (filterKeyAlias) {
      filterKeyAlias[filterKey] = alias;
    }
    this.publishAliasObservableUpdates(setName);
  }

  public registerFilterOptionAlias(
    setName: SearchFilterSetValues,
    filterKey: string,
    optionKey: string,
    alias: string,
  ) {
    const filterOptionAlias = this.filterOptionAliases[setName];
    if (filterOptionAlias) {
      filterOptionAlias[`${filterKey}:${optionKey}`] = alias;
    }
    this.publishAliasObservableUpdates(setName);
  }

  public registerGroupAlias(
    setName: SearchFilterSetValues,
    aliasObject: { values: unknown[]; alias: string },
  ) {
    const registeredSet = this.getRegisteredSet(setName);
    if (registeredSet) {
      let foldingKeys = this.registeredSets[setName]?.foldingKeys ?? [];
      const newAliasObject = aliasObject;
      // remove previous existing aliasObject with the same alias
      foldingKeys = _(foldingKeys).reject((previousAliasObject) => {
        return previousAliasObject.alias === newAliasObject.alias;
      });
      foldingKeys.push(newAliasObject);
      registeredSet.foldingKeys = foldingKeys;

      this.publishAliasObservableUpdates(setName);
      this.updateSet(setName, registeredSet.params);
    } else {
      this.$log.warn(`No set with name=${setName}`);
    }
  }

  public registerFilterComparator(
    setName: SearchFilterSetValues,
    categoryKey: string,
    comparatorFn: (args: unknown) => unknown,
  ) {
    const registeredSet = this.registeredSets[setName];
    if (registeredSet) {
      registeredSet.comparators[categoryKey] = comparatorFn;
    }
  }

  public getFilterComparator(
    setName: SearchFilterSetValues,
    categoryKey: string,
  ) {
    const defaultComparator = function (val: unknown) {
      return val;
    };
    return (
      this.registeredSets[setName]?.comparators[categoryKey] ??
      defaultComparator
    );
  }

  public setExcludedKeys(
    setName: SearchFilterSetValues,
    excludedKeys: string[],
  ) {
    const registeredSet = this.registeredSets[setName];
    if (registeredSet) {
      registeredSet.excludedKeys = excludedKeys;
    }
  }

  /**
   * Removes a single option from a filter set
   *
   * @param setName       The set name which you are clearing from
   * @param categoryKey   The category key which you are clearing from
   * @param optionValues   The list of options that you are removing from the requested category
   */
  public clearFilter(
    setName: SearchFilterSetValues,
    categoryKey: string,
    optionValues: unknown[],
  ) {
    const registeredSet = this.registeredSets[setName];

    if (registeredSet) {
      const filter = registeredSet.params[categoryKey];
      const newFilterParams = _(filter).reject((option) => {
        return _(optionValues).some((ov) => {
          const comparable = this.getFilterComparator(setName, categoryKey);
          return comparable(ov) === comparable(option);
        });
      });

      registeredSet.params = PojoMap.set(
        registeredSet.params,
        categoryKey,
        newFilterParams,
      );
      this.updateSet(setName, registeredSet.params);
    }
  }

  public removeFilter(setName: SearchFilterSetValues, categoryKey: string) {
    const registeredSet = this.registeredSets[setName];

    if (registeredSet) {
      registeredSet.params = _(registeredSet.params).omit(
        categoryKey,
      ) as PojoMap<string, unknown[]>;
      this.updateSet(setName, registeredSet.params);
    }
  }

  /**
   * Sets the options on a category key. Note that this is an overwrite.
   *
   * @param setName       The set name which you are setting to
   * @param categoryKey   The category key which you are setting to
   * @param optionValues   The list of options that you are setting.
   */
  public setFilter(
    setName: SearchFilterSetValues,
    categoryKey: string,
    optionValues: unknown[],
  ) {
    const registeredSet = this.registeredSets[setName];

    if (registeredSet) {
      registeredSet.params = PojoMap.set(
        registeredSet.params,
        categoryKey,
        optionValues,
      );
      this.updateSet(setName, registeredSet.params);
    }
  }

  public registerLockedKeys(
    setName: SearchFilterSetValues,
    newLockedKeys?: unknown[],
  ) {
    if (newLockedKeys) {
      this.lockedKeys = PojoMap.set(
        this.lockedKeys,
        setName,
        this.lockedKeys[setName]?.concat(newLockedKeys) ?? [],
      );
    }
  }

  public getLockedKeys(setName: SearchFilterSetValues) {
    return this.lockedKeys[setName];
  }

  /**
   * Clears all options for a set of category keys
   *
   * @param setName       The set name which you are clearing from
   * @param keyNames      A list of category keys, which all set options will be removed from
   */
  public clearFiltersForKeys(
    setName: SearchFilterSetValues,
    keyNames: string[],
  ) {
    const registeredSet = this.registeredSets[setName];

    if (registeredSet) {
      _(registeredSet.params).each((value, key: string) => {
        if (_(keyNames).contains(key)) {
          registeredSet.params = PojoMap.set(registeredSet.params, key, []);
        }
      });

      this.updateSet(setName, registeredSet.params);
    }
  }

  public clearAllFilters(setName: SearchFilterSetValues) {
    const registeredSet = this.registeredSets[setName];
    if (registeredSet) {
      const excludedKeys = registeredSet.excludedKeys;
      const allFilterKeys = _(registeredSet.params).keys();
      const filterKeysToClear = _(allFilterKeys).reject((filterKey) => {
        return (
          _(excludedKeys).contains(filterKey) ||
          _(this.getLockedKeys(setName)).contains(filterKey)
        );
      });
      if (filterKeysToClear.length > 0) {
        this.clearFiltersForKeys(setName, filterKeysToClear);
      }
    }
  }

  public searchFilterUpdateToTrackingAction(update: SearchFilterUpdateValues) {
    let action;
    switch (update) {
      case this.SearchFilterUpdates.ADD: {
        action = this.TrackingService.ACTION_ADD_FILTER;
        break;
      }
      case this.SearchFilterUpdates.REMOVE: {
        action = this.TrackingService.ACTION_REMOVE_FILTER;
        break;
      }
      case this.SearchFilterUpdates.SET: {
        action = this.TrackingService.ACTION_SET_FILTER;
        break;
      }
      default: {
        throw Error(`Invalid search filter update: ${update}`);
      }
    }

    return action;
  }
}

const curalate = angular.module('curalate');

curalate.service('SearchFilterService', [
  '$log',
  'rx',
  'SearchFilterUpdates',
  'SEARCH_FILTERS',
  'SessionVarsService',
  'TrackingService',
  SearchFilterService,
]);
